import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatError, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Plus,
  Trash,
  PencilSimple,
  FilePdf,
  FileDoc,
  Eye,
  FileText,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_a323fd97-8209-4ccf-b91c-22f7e7a3b086/artifacts/sfihrkca_WhatsApp%20Image%202026-04-22%20at%2022.40.09.jpeg";

const COMPANY = {
  name: "Modolo Multiservices S.L.",
  address: "Calle, Unión Europea, 10 Planta 3B, Los Montesinos - Alicante",
  phone: "674 65 23 91",
  email: "modolo.multiservices@gmail.com",
  nif: "B27559293",
  iban: "ES30 2100 1814 280201017774",
  bic: "CAIXESBBXXX",
  titular: "Modolo Multiservices S.L.",
};

export default function Documentos({ tipo }) {
  // tipo: "presupuesto" | "factura"
  const { t } = useTranslation();
  const isPresupuesto = tipo === "presupuesto";

  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const emptyItem = { cod: "", articulo: "", unidad: 1, precio_un: 0, iva_pct: 21 };
  const emptyForm = {
    tipo,
    cliente_id: "",
    cliente_nombre: "",
    cliente_domicilio: "",
    cliente_ciudad: "",
    cliente_nif: "",
    fecha: new Date().toISOString().split("T")[0],
    comentarios: "",
    forma_pago: "",
    observacoes: "",
    descuento_pct: 0,
    nota: "",
    validez_dias: 30,
    items: [{ ...emptyItem }],
  };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([
        api.get("/documentos", { params: { tipo } }),
        api.get("/clientes"),
      ]);
      setItems(d.data);
      setClientes(c.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    setForm((f) => ({ ...f, tipo }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo]);

  const onClienteChange = (cid) => {
    const cli = clientes.find((c) => c.id === cid);
    setForm({
      ...form,
      cliente_id: cid,
      cliente_nombre: cli?.nome || "",
      cliente_domicilio: cli?.endereco || "",
      cliente_ciudad: cli?.ciudad || "",
      cliente_nif: cli?.nif || "",
    });
  };

  const updateItem = (idx, patch) => {
    const newItems = form.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    setForm({ ...form, items: newItems });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { ...emptyItem }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const totals = useMemo(() => {
    let subtotal = 0;
    form.items.forEach((it) => {
      subtotal += (parseFloat(it.unidad) || 0) * (parseFloat(it.precio_un) || 0);
    });
    const descuento_pct = parseFloat(form.descuento_pct) || 0;
    const descuento_valor = (subtotal * descuento_pct) / 100;
    const base_imponible = subtotal - descuento_valor;
    const iva_pct_main = form.items.length > 0 ? parseFloat(form.items[0].iva_pct) || 21 : 21;
    const iva_valor = (base_imponible * iva_pct_main) / 100;
    const total = base_imponible + iva_valor;
    return {
      subtotal,
      descuento_valor,
      base_imponible,
      iva_pct_main,
      iva_valor,
      total,
    };
  }, [form.items, form.descuento_pct]);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm, tipo, fecha: new Date().toISOString().split("T")[0] });
    setFormOpen(true);
  };

  const openEdit = (doc) => {
    setEditing(doc.id);
    setForm({
      tipo: doc.tipo,
      cliente_id: doc.cliente_id || "",
      cliente_nombre: doc.cliente_nombre || "",
      cliente_domicilio: doc.cliente_domicilio || "",
      cliente_ciudad: doc.cliente_ciudad || "",
      cliente_nif: doc.cliente_nif || "",
      fecha: doc.fecha,
      comentarios: doc.comentarios || "",
      forma_pago: doc.forma_pago || "",
      observacoes: doc.observacoes || "",
      descuento_pct: doc.descuento_pct || 0,
      nota: doc.nota || "",
      validez_dias: doc.validez_dias ?? 30,
      items: (doc.items && doc.items.length ? doc.items : [{ ...emptyItem }]).map((it) => ({ ...emptyItem, ...it })),
    });
    setFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        descuento_pct: parseFloat(form.descuento_pct) || 0,
        validez_dias: parseInt(form.validez_dias) || 30,
        items: form.items
          .filter((it) => it.articulo.trim())
          .map((it) => ({
            cod: it.cod || "",
            articulo: it.articulo,
            unidad: parseFloat(it.unidad) || 0,
            precio_un: parseFloat(it.precio_un) || 0,
            iva_pct: parseFloat(it.iva_pct) || 21,
          })),
      };
      if (editing) {
        await api.put(`/documentos/${editing}`, payload);
      } else {
        await api.post("/documentos", payload);
      }
      toast.success(t("common.success"));
      setFormOpen(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("docs.confirmDelete"))) return;
    await api.delete(`/documentos/${id}`);
    toast.success(t("common.success"));
    load();
  };

  const download = async (id, ext) => {
    const loadingId = toast.loading(ext === "pdf" ? t("docs.downloadPdf") + "..." : t("docs.downloadWord") + "...");
    try {
      const token = localStorage.getItem("modolo_token");
      const res = await fetch(`${API}/documentos/${id}/${ext}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const doc = items.find((x) => x.id === id);
      const filename = `${tipo}_${doc?.numero || id}.${ext}`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
      }, 1500);
      toast.success(filename, { id: loadingId });
    } catch (err) {
      toast.error(`${t("common.error")}: ${err.message}`, { id: loadingId });
    }
  };

  const convertirAfactura = async (id) => {
    if (!window.confirm(t("docs.confirmConvert"))) return;
    try {
      const res = await api.post(`/documentos/${id}/convertir`);
      toast.success(`${t("docs.facturasTitle")} #${res.data.numero} ${t("common.success")}`);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const title = isPresupuesto ? t("docs.presupuestosTitle") : t("docs.facturasTitle");
  const subtitle = isPresupuesto ? t("docs.presupuestosSubtitle") : t("docs.facturasSubtitle");
  const accent = isPresupuesto ? "text-green-700" : "text-red-700";

  return (
    <div data-testid={`${tipo}-page`}>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl heading-font font-bold text-slate-900">{title}</h1>
          <p className="text-slate-500 mt-1 text-sm">{subtitle}</p>
        </div>
        <Button onClick={openAdd} className="bg-green-600 hover:bg-green-700 gap-2" data-testid={`add-${tipo}-btn`}>
          <Plus size={18} weight="bold" /> {t("docs.newDoc")}
        </Button>
      </div>

      {loading ? (
        <Card className="p-10 text-center text-slate-400">{t("common.loading")}</Card>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-slate-200">
          <FileText size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
          {t("docs.empty")}
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("docs.number")}</th>
                  <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("docs.date")}</th>
                  <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("docs.client")}</th>
                  <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("docs.total")}</th>
                  <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("docs.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((doc) => (
                  <tr key={doc.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`doc-row-${doc.id}`}>
                    <td className={`px-4 py-3 text-sm font-bold ${accent}`}>#{doc.numero}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{doc.fecha}</td>
                    <td className="px-4 py-3 text-sm text-slate-900 font-medium">{doc.cliente_nombre || "—"}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-slate-900">€ {(doc.total || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => setViewing(doc)} data-testid={`view-doc-${doc.id}`}>
                        <Eye size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => download(doc.id, "pdf")} data-testid={`pdf-doc-${doc.id}`} title="PDF">
                        <FilePdf size={16} className="text-red-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => download(doc.id, "docx")} data-testid={`docx-doc-${doc.id}`} title="Word">
                        <FileDoc size={16} className="text-blue-600" />
                      </Button>
                      {isPresupuesto && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => convertirAfactura(doc.id)}
                          data-testid={`convert-doc-${doc.id}`}
                          title={t("docs.convertToFactura")}
                          className="text-amber-600 hover:bg-amber-50"
                        >
                          <ArrowsClockwise size={16} />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openEdit(doc)} data-testid={`edit-doc-${doc.id}`}>
                        <PencilSimple size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(doc.id)} data-testid={`delete-doc-${doc.id}`}>
                        <Trash size={16} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ======== FORM DIALOG ======== */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? t("docs.editDoc") : t("docs.newDoc")} — {isPresupuesto ? t("docs.presupuestosTitle") : t("docs.facturasTitle")}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Client + date row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("docs.client")}</Label>
                <Select value={form.cliente_id} onValueChange={onClienteChange}>
                  <SelectTrigger data-testid="doc-cliente-select">
                    <SelectValue placeholder={t("docs.selectClient")} />
                  </SelectTrigger>
                  <SelectContent>
                    {clientes.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder={t("docs.client")}
                  value={form.cliente_nombre}
                  onChange={(e) => setForm({ ...form, cliente_nombre: e.target.value })}
                  data-testid="doc-cliente-nombre"
                />
                <Input
                  placeholder={t("docs.domicilio")}
                  value={form.cliente_domicilio}
                  onChange={(e) => setForm({ ...form, cliente_domicilio: e.target.value })}
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder={t("docs.ciudad")}
                    value={form.cliente_ciudad}
                    onChange={(e) => setForm({ ...form, cliente_ciudad: e.target.value })}
                  />
                  <Input
                    placeholder={t("docs.nif")}
                    value={form.cliente_nif}
                    onChange={(e) => setForm({ ...form, cliente_nif: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("docs.date")}</Label>
                <Input
                  type="date"
                  value={form.fecha}
                  onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                  required
                  data-testid="doc-date-input"
                />
                <Label>{t("docs.comentarios")}</Label>
                <Textarea
                  value={form.comentarios}
                  onChange={(e) => setForm({ ...form, comentarios: e.target.value })}
                  rows={4}
                  placeholder={t("docs.comentarios")}
                  data-testid="doc-comentarios-input"
                />
              </div>
            </div>

            {/* Items table */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base heading-font">{t("docs.items")}</Label>
                <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="add-item-btn">
                  <Plus size={14} className="mr-1" /> {t("docs.addItem")}
                </Button>
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-2 py-2 text-xs font-bold uppercase text-slate-500 w-20">{t("docs.cod")}</th>
                      <th className="px-2 py-2 text-xs font-bold uppercase text-slate-500 text-left">{t("docs.articulo")}</th>
                      <th className="px-2 py-2 text-xs font-bold uppercase text-slate-500 w-20">{t("docs.unidad")}</th>
                      <th className="px-2 py-2 text-xs font-bold uppercase text-slate-500 w-28">{t("docs.precioUn")}</th>
                      <th className="px-2 py-2 text-xs font-bold uppercase text-slate-500 w-20">{t("docs.iva")}</th>
                      <th className="px-2 py-2 text-xs font-bold uppercase text-slate-500 w-28 text-right">{t("docs.subtotal")}</th>
                      <th className="px-2 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, idx) => {
                      const sub = (parseFloat(it.unidad) || 0) * (parseFloat(it.precio_un) || 0);
                      return (
                        <tr key={idx} className="border-t border-slate-100">
                          <td className="px-1 py-1"><Input value={it.cod} onChange={(e) => updateItem(idx, { cod: e.target.value })} className="h-8 text-sm" /></td>
                          <td className="px-1 py-1"><Input value={it.articulo} onChange={(e) => updateItem(idx, { articulo: e.target.value })} className="h-8 text-sm" data-testid={`item-articulo-${idx}`} /></td>
                          <td className="px-1 py-1"><Input type="number" step="0.01" value={it.unidad} onChange={(e) => updateItem(idx, { unidad: e.target.value })} className="h-8 text-sm" /></td>
                          <td className="px-1 py-1"><Input type="number" step="0.01" value={it.precio_un} onChange={(e) => updateItem(idx, { precio_un: e.target.value })} className="h-8 text-sm" data-testid={`item-precio-${idx}`} /></td>
                          <td className="px-1 py-1"><Input type="number" step="0.01" value={it.iva_pct} onChange={(e) => updateItem(idx, { iva_pct: e.target.value })} className="h-8 text-sm" /></td>
                          <td className="px-2 py-1 text-right font-semibold text-slate-800">€ {sub.toFixed(2)}</td>
                          <td className="px-1 py-1">
                            <Button type="button" variant="ghost" size="sm" className="text-red-600 h-8 w-8 p-0" onClick={() => removeItem(idx)}>
                              <Trash size={14} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Nota, forma pago, observacoes, totals */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <Label>{t("docs.nota")}</Label>
                  <Textarea value={form.nota} onChange={(e) => setForm({ ...form, nota: e.target.value })} rows={2} />
                </div>
                {isPresupuesto && (
                  <div>
                    <Label>{t("docs.validez")}</Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.validez_dias}
                      onChange={(e) => setForm({ ...form, validez_dias: e.target.value })}
                      data-testid="validez-input"
                    />
                  </div>
                )}
                <div>
                  <Label>{t("docs.formaPago")}</Label>
                  <Input value={form.forma_pago} onChange={(e) => setForm({ ...form, forma_pago: e.target.value })} />
                </div>
                <div>
                  <Label>{t("docs.observacoes")}</Label>
                  <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} rows={2} />
                </div>
              </div>

              <div className="space-y-2 bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t("docs.subtotal")}:</span>
                  <span className="font-semibold">€ {totals.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-600">{t("docs.descuento")}:</span>
                  <Input type="number" step="0.01" value={form.descuento_pct} onChange={(e) => setForm({ ...form, descuento_pct: e.target.value })} className="h-7 w-20 text-right text-sm" />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t("docs.totalDescuento")}:</span>
                  <span className="font-semibold">€ {totals.descuento_valor.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t("docs.baseImponible")}:</span>
                  <span className="font-semibold">€ {totals.base_imponible.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t("docs.ivaValor")} ({totals.iva_pct_main}%):</span>
                  <span className="font-semibold">€ {totals.iva_valor.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-slate-300">
                  <span className="heading-font font-bold text-red-700">{t("docs.grandTotal")}:</span>
                  <span className="heading-font font-bold text-red-700 text-lg">€ {totals.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                {t("docs.cancel")}
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="doc-save-btn">
                {t("docs.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ======== PREVIEW DIALOG ======== */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isPresupuesto ? t("docs.presupuestosTitle") : t("docs.facturasTitle")} #{viewing?.numero}
            </DialogTitle>
          </DialogHeader>
          {viewing && <PreviewDoc doc={viewing} tipoLabel={isPresupuesto ? "Presupuesto" : "Factura"} />}
          {viewing && <PaymentsBlock docId={viewing.id} />}
          <DialogFooter>
            {isPresupuesto && viewing && (
              <Button
                variant="outline"
                onClick={() => { convertirAfactura(viewing.id); setViewing(null); }}
                className="gap-1 text-amber-700 border-amber-300 hover:bg-amber-50"
                data-testid="convert-preview-btn"
              >
                <ArrowsClockwise size={16} /> {t("docs.convertToFactura")}
              </Button>
            )}
            <Button variant="outline" onClick={() => download(viewing.id, "pdf")} className="gap-1">
              <FilePdf size={16} className="text-red-600" /> {t("docs.downloadPdf")}
            </Button>
            <Button variant="outline" onClick={() => download(viewing.id, "docx")} className="gap-1">
              <FileDoc size={16} className="text-blue-600" /> {t("docs.downloadWord")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PreviewDoc({ doc, tipoLabel }) {
  const iva_pct_main = doc.items?.[0]?.iva_pct ?? 21;
  return (
    <div className="bg-white p-6 border-2 border-red-300 rounded-lg text-sm">
      {/* Header */}
      <div className="grid grid-cols-[140px_1fr] gap-3 border-2 border-red-500 p-3">
        <img src={LOGO_URL} alt="Modolo" className="w-full h-32 object-contain" />
        <div>
          <div className="font-bold text-sm">{COMPANY.name}</div>
          <div className="text-xs">{COMPANY.address}</div>
          <div className="text-xs mt-1">Tel: {COMPANY.phone} · {COMPANY.email}</div>
          <div className="text-xs"><span className="font-bold">Nº Cuenta:</span> {COMPANY.iban}</div>
          <div className="grid grid-cols-2 mt-2 pt-2 border-t border-red-300">
            <div>
              <span className="font-bold">{tipoLabel}:</span> <span className="text-xl font-bold">{doc.numero}</span>
            </div>
            <div className="text-right">
              <div><span className="font-bold">Fecha:</span> {doc.fecha}</div>
              <div><span className="font-bold">NIF:</span> {COMPANY.nif}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Client */}
      <div className="grid grid-cols-[1fr_1fr] border-2 border-t-0 border-red-500">
        <div className="p-3 border-r border-red-500">
          <div><span className="font-bold">Cliente:</span> {doc.cliente_nombre}</div>
          <div><span className="font-bold">Domicilio:</span> {doc.cliente_domicilio}</div>
          <div><span className="font-bold">Ciudad:</span> {doc.cliente_ciudad}</div>
          <div><span className="font-bold">N.I.F:</span> {doc.cliente_nif}</div>
        </div>
        <div className="p-3">
          <div className="font-bold">Comentarios</div>
          <div>{doc.comentarios}</div>
        </div>
      </div>

      {/* Items */}
      <table className="w-full border-2 border-t-0 border-red-500 text-xs">
        <thead className="bg-orange-50">
          <tr>
            <th className="border border-red-300 px-2 py-1">Cód.</th>
            <th className="border border-red-300 px-2 py-1 text-left">Artículo</th>
            <th className="border border-red-300 px-2 py-1">Unid.</th>
            <th className="border border-red-300 px-2 py-1">Precio Un.</th>
            <th className="border border-red-300 px-2 py-1">Subtotal</th>
            <th className="border border-red-300 px-2 py-1">%IVA</th>
            <th className="border border-red-300 px-2 py-1">Total c/IVA</th>
          </tr>
        </thead>
        <tbody>
          {(doc.items || []).map((it, i) => {
            const sub = (it.unidad || 0) * (it.precio_un || 0);
            const total = sub * (1 + (it.iva_pct || 21) / 100);
            return (
              <tr key={i}>
                <td className="border border-red-200 px-2 py-1 text-center">{it.cod}</td>
                <td className="border border-red-200 px-2 py-1">{it.articulo}</td>
                <td className="border border-red-200 px-2 py-1 text-right">{it.unidad}</td>
                <td className="border border-red-200 px-2 py-1 text-right">€ {(it.precio_un || 0).toFixed(2)}</td>
                <td className="border border-red-200 px-2 py-1 text-right">€ {sub.toFixed(2)}</td>
                <td className="border border-red-200 px-2 py-1 text-right">{it.iva_pct}%</td>
                <td className="border border-red-200 px-2 py-1 text-right">€ {total.toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {doc.nota && <div className="mt-2 text-xs"><span className="font-bold">NOTA:</span> {doc.nota}</div>}
      {doc.observacoes && <div className="mt-2 text-xs"><span className="font-bold">Observações:</span> {doc.observacoes}</div>}
      {doc.tipo === "presupuesto" && (
        <div className="mt-2 text-xs text-red-700">
          <span className="font-bold">Validez de la oferta:</span> {doc.validez_dias ?? 30} días desde la fecha de emisión
        </div>
      )}

      {/* Totals */}
      <div className="grid grid-cols-[1fr_1fr] mt-4 border-2 border-red-500">
        <div className="p-3 border-r border-red-500">
          <div><span className="font-bold">Forma Pago:</span> {doc.forma_pago}</div>
          <div className="mt-2 pt-2 border-t border-red-200 text-xs">
            <div className="font-bold">Datos bancarios para pago:</div>
            <div>Nº de cuenta: {COMPANY.iban}</div>
            <div>BIC/SWIFT: {COMPANY.bic}</div>
            <div>Titular: {COMPANY.titular}</div>
          </div>
        </div>
        <div className="p-3 space-y-1 text-xs">
          <div className="flex justify-between"><span className="font-bold">Subtotal:</span><span>€ {doc.subtotal?.toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="font-bold">Descuento:</span><span>{doc.descuento_pct}%</span></div>
          <div className="flex justify-between"><span className="font-bold">Total Descuento €:</span><span>€ {doc.descuento_valor?.toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="font-bold">Base Imponible:</span><span>€ {doc.base_imponible?.toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="font-bold">I.V.A. - %:</span><span>{iva_pct_main}%</span></div>
          <div className="flex justify-between"><span className="font-bold">I.V.A. - €:</span><span>€ {doc.iva_valor?.toFixed(2)}</span></div>
          <div className="flex justify-between pt-2 border-t border-red-400 text-base">
            <span className="font-bold">Total:</span>
            <span className="font-bold text-red-700">€ {doc.total?.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PaymentsBlock({ docId }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/documentos/${docId}/pagos`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [docId]);
  if (!data) return null;
  const colors = {
    pagado: "bg-green-100 text-green-800 border-green-300",
    parcial: "bg-amber-100 text-amber-800 border-amber-300",
    pendiente: "bg-slate-100 text-slate-700 border-slate-300",
  };
  const statusKey = data.status === "pagado" ? "statusPagado" : data.status === "parcial" ? "statusParcial" : "statusPendiente";
  return (
    <div className="mt-4 border rounded-lg p-4 bg-slate-50">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="font-bold text-slate-900">{t("ingresos.paymentsLabel")}</div>
        <div className={`px-3 py-1 rounded-full text-xs font-bold border ${colors[data.status]}`} data-testid={`doc-pago-status-${data.status}`}>
          {t(`ingresos.${statusKey}`)}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-white border rounded p-2 text-center">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("docs.total")}</div>
          <div className="text-base font-bold text-slate-900">€ {Number(data.total_doc).toFixed(2)}</div>
        </div>
        <div className="bg-white border rounded p-2 text-center">
          <div className="text-[10px] uppercase tracking-wider text-green-700">{t("ingresos.paid")}</div>
          <div className="text-base font-bold text-green-700">€ {Number(data.total_pagado).toFixed(2)}</div>
        </div>
        <div className="bg-white border rounded p-2 text-center">
          <div className="text-[10px] uppercase tracking-wider text-red-700">{t("ingresos.pending")}</div>
          <div className="text-base font-bold text-red-700">€ {Number(data.pendiente).toFixed(2)}</div>
        </div>
      </div>
      {data.ingresos.length > 0 && (
        <div className="space-y-1">
          {data.ingresos.map((it) => (
            <div key={it.id} className="flex items-center justify-between text-xs bg-white border rounded px-3 py-1.5">
              <div className="flex items-center gap-2">
                <span className="text-slate-500 font-medium">{it.fecha}</span>
                <span className="bg-green-100 text-green-800 rounded px-1.5 py-0.5 text-[10px]">{t(`ingresos.metodos.${it.metodo_pago}`)}</span>
                {it.descripcion && <span className="text-slate-600">— {it.descripcion}</span>}
              </div>
              <div className="font-bold text-green-700">€ {Number(it.monto).toFixed(2)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
